/**
 * Pure helper for applying array-token transforms.
 *
 * When the dragged-in token carries `arrayDetails.implicitForeachExpression`,
 * the surrounding action needs to be wrapped in a ForEach and the token
 * rewritten to use `item()`. The wrapping itself is a store mutation, so
 * this util takes the wrap/notify callbacks as injected dependencies — the
 * store-orchestrating front door lives in `hooks/useApplyTokenTransforms`.
 *
 * Keeping the compute pure means it can be unit-tested with plain inputs
 * and any other surface (extension, command-palette action, AI agent) can
 * reuse the rewriting logic with its own wiring.
 */

import type { Token } from '@/types/valueSegment';
import type { SelectedNode } from '@/stores/nodeEditorStore';

/**
 * Dependencies the caller injects for the {@link applyArrayTokenTransforms}
 * compute. All store/service access is funneled through these callbacks so
 * the helper itself can stay store-free.
 */
export interface ApplyArrayTokenTransformsContext {
  /** Currently selected node (or null when nothing is selected). */
  selectedNode: SelectedNode | null;
  /**
   * Wrap the named action in an implicit ForEach. Returns the loop name and
   * whether a new wrapper was created (vs reusing an existing ancestor).
   * Returns `null` when wrapping isn't possible.
   */
  wrapActionInImplicitForeach: (
    actionName: string,
    implicitForeachExpression: string,
  ) => { loopName: string; created: boolean } | null;
  /**
   * Optional side effect invoked exactly when a NEW ForEach wrapper is
   * created. The hook wrapper uses this to flush cached `runAfter` state
   * on the wrapped node. Not called when reusing an existing ancestor.
   */
  onForeachCreated?: () => void;
}

export function applyArrayTokenTransforms(
  token: Token,
  ctx: ApplyArrayTokenTransformsContext,
): Token {
  const node = ctx.selectedNode;

  if (!node || node.type !== 'action' || !node.name || !token.arrayDetails) {
    return token;
  }

  return applyImplicitForeachIfNeeded(token, node, ctx);
}

/**
 * If the token references a property inside an array, wrap the target action
 * in a ForEach and rewrite the token value to use `item()`.
 *
 * Returns the (possibly transformed) token. The original token is never mutated.
 */
function applyImplicitForeachIfNeeded(
  token: Token,
  node: SelectedNode,
  ctx: ApplyArrayTokenTransformsContext,
): Token {
  const implicitForeachExpression = token.arrayDetails!.implicitForeachExpression;
  const implicitItemPath = token.arrayDetails!.implicitItemPath;

  if (!implicitForeachExpression || !implicitItemPath) {
    return token;
  }

  const result = ctx.wrapActionInImplicitForeach(node.name, implicitForeachExpression);
  if (!result) {
    return token;
  }

  // Only fire the runAfter-flush callback when a new ForEach wrapper was created.
  if (result.created) {
    ctx.onForeachCreated?.();
  }

  return {
    ...token,
    value: `\${item()${implicitItemPath}}`,
    arrayDetails: {
      ...token.arrayDetails,
      loopSource: result.loopName,
    },
  };
}
